import React, { useState, useEffect } from 'react';
import { X, Clock, Hand, Flame, Check } from 'lucide-react';
import { BakingSession, FERMENTATION_PROCESSES } from '../../types/baking';
import ShapingIcon from '../layout/ShapingIcon';

interface SessionTimelinePanelProps {
  session: BakingSession;
  isOpen: boolean;
  onClose: () => void;
}

export function SessionTimelinePanel({
  session,
  isOpen,
  onClose,
}: SessionTimelinePanelProps) {
  const [currentStep, setCurrentStep] = useState<
    'kneading' | 'bulk' | 'shaping' | 'proofing' | 'baking' | 'completed'
  >('kneading');
  const [timeToNext, setTimeToNext] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;

    const updateCurrentStep = () => {
      const now = new Date();
      const process = FERMENTATION_PROCESSES.find(
        (p) => p.id === session.fermentationType,
      );
      if (!process) return;

      const bakeTime = new Date(session.targetBakeTime);

      // Use custom times if available
      let totalTime = process.totalTime;
      let bulkTime = process.bulkTime;
      let proofTime = process.proofTime;

      if (session.fermentationType === 'cold-bulk' && session.customBulkTime) {
        totalTime = session.customBulkTime + process.proofTime;
        bulkTime = session.customBulkTime;
      } else if (
        session.fermentationType === 'pre-proofed' &&
        session.customProofTime
      ) {
        totalTime = process.bulkTime + session.customProofTime;
        proofTime = session.customProofTime;
      }

      const kneadingTime = new Date(bakeTime);
      kneadingTime.setHours(kneadingTime.getHours() - totalTime);

      const shapingTime = new Date(bakeTime);
      shapingTime.setHours(shapingTime.getHours() - proofTime);

      // Determine current step
      if (now < kneadingTime) {
        setCurrentStep('kneading');
        setTimeToNext(
          formatTimeRemaining(kneadingTime.getTime() - now.getTime()),
        );
      } else if (now < shapingTime) {
        setCurrentStep('bulk');
        setTimeToNext(
          formatTimeRemaining(shapingTime.getTime() - now.getTime()),
        );
      } else if (now < bakeTime) {
        setCurrentStep('proofing');
        setTimeToNext(formatTimeRemaining(bakeTime.getTime() - now.getTime()));
      } else {
        setCurrentStep('baking');
        setTimeToNext('Now!');
      }
    };

    updateCurrentStep();
    const interval = setInterval(updateCurrentStep, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [session, isOpen]);

  const formatTimeRemaining = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor(
      (milliseconds % (1000 * 60 * 60)) / (1000 * 60),
    );
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getSessionTimes = () => {
    const process = FERMENTATION_PROCESSES.find(
      (p) => p.id === session.fermentationType,
    );
    if (!process) return null;

    const bakeTime = new Date(session.targetBakeTime);

    let totalTime = process.totalTime;
    let bulkTime = process.bulkTime;
    let proofTime = process.proofTime;

    // Use custom times if available
    if (session.fermentationType === 'cold-bulk' && session.customBulkTime) {
      totalTime = session.customBulkTime + process.proofTime;
      bulkTime = session.customBulkTime;
    } else if (
      session.fermentationType === 'pre-proofed' &&
      session.customProofTime
    ) {
      totalTime = process.bulkTime + session.customProofTime;
      proofTime = session.customProofTime;
    }

    const kneadingTime = new Date(bakeTime);
    kneadingTime.setHours(kneadingTime.getHours() - totalTime);

    const shapingTime = new Date(bakeTime);
    shapingTime.setHours(shapingTime.getHours() - proofTime);

    return { kneading: kneadingTime, shaping: shapingTime, baking: bakeTime, bulkTime, proofTime };
  };

  const times = getSessionTimes();
  if (!isOpen || !times) return null;

  const getStepStatus = (step: string) => {
    const stepOrder = ['kneading', 'bulk', 'shaping', 'proofing', 'baking'];
    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(step);

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const getTimeDisplay = (step: string) => {
    return timeToNext;
  };

  return (
    <div className="fixed bottom-24 md:bottom-8 right-4 z-20 w-80">
      <div className="bg-white/90 rounded-br-[50px] shadow-xl max-h-[80vh]">
        <div className="p-4 space-y-4">
          {/* Kneading Step */}
          <div
            className={`p-3 rounded-xl border transition-all duration-300 ${
              getStepStatus('kneading') === 'completed'
                ? 'bg-blue-50 border-black/20'
                : getStepStatus('kneading') === 'current'
                  ? 'bg-blue-100 border-blue-300'
                  : 'bg-gray-50 border-gray-200'
            } relative`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  getStepStatus('kneading') === 'completed'
                    ? 'bg-blue-500'
                    : getStepStatus('kneading') === 'current'
                      ? 'bg-blue-600'
                      : 'bg-gray-300'
                }`}
              >
                <Hand
                  className={`w-5 h-5 ${
                    getStepStatus('kneading') === 'upcoming'
                      ? 'text-white/50'
                      : 'text-white'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3
                  className={`font-medium ${
                    getStepStatus('kneading') === 'upcoming'
                      ? 'text-gray-900/50'
                      : 'text-gray-900'
                  }`}
                >
                  Kneading
                </h3>
                <p
                  className={`text-sm ${
                    getStepStatus('kneading') === 'upcoming'
                      ? 'text-gray-600/50'
                      : 'text-gray-600'
                  }`}
                >
                  {formatDateTime(times.kneading)}
                </p>
                {getStepStatus('kneading') === 'current' && (
                  <p className="text-sm font-medium text-blue-600">
                    {getTimeDisplay('kneading')} left
                  </p>
                )}
              </div>
            </div>
            {getStepStatus('kneading') === 'completed' && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Bulk Fermentation Duration */}
          <div
            className={`p-4 flex items-center rounded-xl border border-black/20 gap-2 text-sm relative ${
              currentStep === 'bulk' ? 'text-gray-600' : 'text-gray-600'
            }`}
          >
            <Clock
              className={`w-5 h-5 ${
                currentStep === 'bulk' ? 'text-gray-600' : 'text-gray-600'
              }`}
            />
            <span>+{times.bulkTime}h (Bulk ferm.)</span>
            {currentStep === 'bulk' && (
              <span className="ml-auto whitespace-nowrap text-xs font-medium bg-blue-500 text-white px-2 py-1 animate-pulse rounded-full ml-2">
                • Current step
              </span>
            )}
            {getStepStatus('bulk') === 'completed' && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Shaping Step */}
          <div
            className={`p-3 rounded-xl border transition-all duration-300 ${
              getStepStatus('shaping') === 'completed'
                ? 'bg-green-50 border-black/20'
                : getStepStatus('shaping') === 'current'
                  ? 'bg-green-50 border-green-300'
                  : 'bg-gray-50 border-gray-200'
            } relative`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  getStepStatus('shaping') === 'completed'
                    ? 'bg-green-500'
                    : getStepStatus('shaping') === 'current'
                      ? 'bg-green-600'
                      : 'bg-gray-300'
                }`}
              >
                <ShapingIcon
                  className={`w-5 h-5 ${
                    getStepStatus('shaping') === 'upcoming'
                      ? 'text-white/50'
                      : 'text-white'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3
                  className={`font-medium ${
                    getStepStatus('shaping') === 'upcoming'
                      ? 'text-gray-900/50'
                      : 'text-gray-900'
                  }`}
                >
                  Shaping
                </h3>
                <p
                  className={`text-sm ${
                    getStepStatus('shaping') === 'upcoming'
                      ? 'text-gray-600/50'
                      : 'text-gray-600'
                  }`}
                >
                  {formatDateTime(times.shaping)}
                </p>
                {getStepStatus('shaping') === 'current' && (
                  <p className="text-sm font-medium text-green-600">
                    {getTimeDisplay('shaping')} left
                  </p>
                )}
              </div>
            </div>
            {getStepStatus('shaping') === 'completed' && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Proofing Duration */}
          <div
            className={`p-4 rounded-xl border border-black/20 flex items-center gap-2 text-sm relative ${
              currentStep === 'proofing' ? 'text-gray-600' : 'text-gray-600/50'
            }`}
          >
            <Clock
              className={`w-5 h-5 ${
                currentStep === 'proofing' ? 'text-gray-600' : 'text-gray-600/50'
              }`}
            />
            <span>+{times.proofTime}h (Proofing)</span>
            {currentStep === 'proofing' && (
              <span className="ml-auto whitespace-nowrap text-xs font-medium bg-blue-500 text-white px-2 py-1 rounded-full animate-pulse ml-2">
                • Current step
              </span>
            )}
            {getStepStatus('proofing') === 'completed' && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Baking Step */}
          <div
            className={`p-3 rounded-xl border transition-all duration-300 ${
              getStepStatus('baking') === 'completed'
                ? 'bg-orange-50 border-black/20'
                : getStepStatus('baking') === 'current'
                  ? 'bg-orange-100 border-orange-300'
                  : 'bg-gray-50 border-gray-200'
            } relative`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  getStepStatus('baking') === 'completed'
                    ? 'bg-orange-500'
                    : getStepStatus('baking') === 'current'
                      ? 'bg-orange-600'
                      : 'bg-gray-300'
                }`}
              >
                <Flame
                  className={`w-5 h-5 ${
                    getStepStatus('baking') === 'upcoming'
                      ? 'text-white/50'
                      : 'text-white'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3
                  className={`font-medium ${
                    getStepStatus('baking') === 'upcoming'
                      ? 'text-gray-900/50'
                      : 'text-gray-900'
                  }`}
                >
                  Baking
                </h3>
                <p
                  className={`text-sm ${
                    getStepStatus('baking') === 'upcoming'
                      ? 'text-gray-600/50'
                      : 'text-gray-600'
                  }`}
                >
                  {formatDateTime(times.baking)}
                </p>
                {getStepStatus('baking') === 'current' && (
                  <p className="text-sm font-medium text-orange-600">
                    Time to bake!
                  </p>
                )}
              </div>
            </div>
            {getStepStatus('baking') === 'completed' && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Session name */}
          <div className="p-2 rounded-xl border border-black/20">
            <h2 className="text-2xl text-gray-800 font-brand font-light italic">
              {session.name}
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
